<div class="container-xxl p-3">
  <div class="d-flex align-items-center mb-2">
    <h2 class="text-color mb-0">
      {{ 'auth.monitored_users.title' | translate }}
    </h2>

    <button
      class="btn btn-primary d-flex ms-auto"
      type="button"
      (click)="handleOpenInvitePatientModal()"
    >
      {{ 'monitoring.users.add_patient_user' | translate }}
    </button>
  </div>

  <div>
    <input
      matInput
      placeholder="{{ 'monitoring.search' | translate }}"
      class="search-field form-control"
      (keyup)="applyFilter($event)"
    />
  </div>

  <div class="card p-2 mt-2 mb-5 table-wrapper">
    <table mat-table [dataSource]="dataSource" matSort>
      <!-- Name Column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 200px">
          {{ 'monitoring.profile_data.name' | translate }}
        </th>
        <td mat-cell *matCellDef="let user" style="min-width: 200px">
          {{ user.lastName }}, {{ user.firstName }}
        </td>
      </ng-container>

      <!-- Last Activity Column (ticket 27757) -->
      <ng-container matColumnDef="lastActivity">
        <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 120px">
          Letzte Aktivität
        </th>
        <td mat-cell *matCellDef="let user" style="min-width: 120px">
          <ng-container *ngIf="lastActivityOf(user) as activity; else noActivity">
            <div class="fw-bold">{{ formatDate(activity) }}</div>
            <div class="text-muted small">{{ relativeDate(activity) }}</div>
          </ng-container>
          <ng-template #noActivity>–</ng-template>
        </td>
      </ng-container>

      <!-- Flow Column -->
      <ng-container matColumnDef="flow">
          <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 80px">
            {{ 'monitoring.profile_data.flow' | translate }}
          </th>
          <td mat-cell *matCellDef="let user" style="min-width: 80px">
            <ng-container *ngIf="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Confirmed; else flowHidden">
              <img
                [src]="'../../../../../assets/images/' + (user.flow || 'default' | lowercase) + '-flow.svg'"
                [alt]="(user.flow || 'default') + ' flow'"
              />
            </ng-container>
            <ng-template #flowHidden>–</ng-template>
          </td>
      </ng-container>

      <!-- BMI Column -->
      <ng-container matColumnDef="bmi">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'monitoring.profile_data.bmi' | translate }}
        </th>
        <td mat-cell *matCellDef="let user">
          <ng-container *ngIf="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Confirmed && user.scoreDetails?.bmi !== undefined; else bmiHidden">
            {{ user.scoreDetails.bmi - (user.scoreDetails.bmi % 1) }} {{ 'monitoring.profile_data.bmi_scale' | translate }}
          </ng-container>
          <ng-template #bmiHidden>–</ng-template>
        </td>
      </ng-container>

      <!-- Weight Column -->
      <ng-container matColumnDef="weight">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'monitoring.profile_data.weight' | translate }}
        </th>
        <td mat-cell *matCellDef="let user">
          <ng-container *ngIf="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Confirmed && user.scoreDetails?.weightChangePercentage !== undefined; else weightHidden">
            {{ user.scoreDetails.weightChangePercentage >= 0 ? '+' : '' }}{{ user.scoreDetails.weightChangePercentage }}%
          </ng-container>
          <ng-template #weightHidden>–</ng-template>
        </td>
      </ng-container>

      <!-- Last Weight Entry Column (ticket 27757) -->
      <ng-container matColumnDef="lastWeightDate">
        <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 120px">
          Letztes Gewicht
        </th>
        <td mat-cell *matCellDef="let user" style="min-width: 120px">
          <ng-container *ngIf="user.lastWeightDate; else noWeightDate">
            <div class="fw-bold">{{ formatDate(user.lastWeightDate) }}</div>
            <div class="text-muted small">{{ relativeDate(user.lastWeightDate) }}</div>
          </ng-container>
          <ng-template #noWeightDate>–</ng-template>
        </td>
      </ng-container>

      <!-- Ernährungsbeschwerden Column -->
      <ng-container matColumnDef="nutritionIssues">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'monitoring.profile_data.nutrition_issues' | translate }}
        </th>
        <td mat-cell *matCellDef="let user">
          <ng-container *ngIf="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Confirmed && user.scoreDetails?.strasserScore !== undefined; else complaintsHidden">
            {{ getStrasserScoreTranslationKey(user.scoreDetails.strasserScore) | translate }}
          </ng-container>
          <ng-template #complaintsHidden>–</ng-template>
        </td>
      </ng-container>

      <!-- Last Survey Entry Column (ticket 27757) -->
      <ng-container matColumnDef="lastSurveyDate">
        <th mat-header-cell *matHeaderCellDef mat-sort-header style="min-width: 120px">
          Letzte Befragung
        </th>
        <td mat-cell *matCellDef="let user" style="min-width: 120px">
          <ng-container *ngIf="user.lastSurveyDate; else noSurveyDate">
            <div class="fw-bold">{{ formatDate(user.lastSurveyDate) }}</div>
            <div class="text-muted small">{{ relativeDate(user.lastSurveyDate) }}</div>
          </ng-container>
          <ng-template #noSurveyDate>–</ng-template>
        </td>
      </ng-container>

      <!-- Connection Status Column -->
      <ng-container matColumnDef="monitorConnectionStatus">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'monitoring.profile_data.connection_status' | translate }}
        </th>
        <td mat-cell *matCellDef="let user">
          <ng-container *ngIf="user.monitorConnectionStatus">
            {{ 'monitoring.connection_status.' + user.monitorConnectionStatus | translate }}
          </ng-container>
          <ng-container *ngIf="!user.monitorConnectionStatus"> Not connected </ng-container>
        </td>
      </ng-container>

      <!-- Subadvisor Column -->
      <ng-container matColumnDef="subadvisor">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'monitoring.profile_data.allowed_monitoring_users' | translate }}
        </th>
        <td mat-cell *matCellDef="let user">
          {{ getAllowedMonitoringUsersText(user) }}
        </td>
      </ng-container>

      <!-- Remove User Button Column -->
      <ng-container matColumnDef="remove">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let user">
          <ng-container *ngIf="canRemoveUser(user)">
            <ng-container
              *ngIf="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Confirmed"
            >
              <div
                class="d-flex justify-content-center align-items-center remove-button"
                [inlineSVG]="'assets/images/remove-icon.svg'"
                (click)="handleRemoveMonitoredUsers([user.id]); $event.stopPropagation()"
              ></div>
            </ng-container>
          </ng-container>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr
        mat-row
        *matRowDef="let user; columns: displayedColumns"
        [class.disabled]="user.monitorConnectionStatus === MONITOR_CONNECTION_STATUS.Invited"
        class="cursor-pointer"
        (click)="handleOpenUserDetails(user)"
      ></tr>
    </table>
  </div>
</div>
